// IP module → "Discharged": every closed admission at this branch, searchable.
// A discharged patient's bed is free again, so without this list there is no
// way back to their Case Sheet. Opening a row shows the same Case Sheet
// workspace the ward uses — read-only, with Generate case sheet in its header.
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable';
import { patientDisplayName } from '@/lib/patientName';
import { useAdmissionList } from './hooks';
import type { AdmissionListRow } from './api';
import { CaseSheetStatusBadge, SummaryStatusBadge, dischargeTypeLabel, fmtDate, stayDays } from './ui';

const PAGE_SIZE = 25;

export function DischargedAdmissionsList({ onOpen }: { onOpen: (row: AdmissionListRow) => void }) {
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);

  // Debounced so typing a name doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => { setQ(search.trim()); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const list = useAdmissionList({
    status: 'discharged', q: q || undefined, from: from || undefined, to: to || undefined,
    page, limit: PAGE_SIZE,
  });
  const rows = list.data?.data ?? [];
  const total = list.data?.total ?? null;
  const badRange = !!from && !!to && from > to;

  const columns: DataTableColumn<AdmissionListRow>[] = [
    {
      key: 'patient', header: 'Patient',
      render: (r) => (
        <div className="min-w-0">
          <div className="font-semibold text-ink">{patientDisplayName({ name: r.patient_name, title: r.patient_title })}</div>
          <div className="text-[11.5px] text-ink-muted">{r.patient_uhid}{r.ip_number ? ` · ${r.ip_number}` : ''}</div>
        </div>
      ),
    },
    {
      key: 'stay', header: 'Stay',
      render: (r) => (
        <div className="text-[12.5px]">
          <div>{fmtDate(r.admitted_at)} → {fmtDate(r.discharged_at)}</div>
          <div className="text-[11.5px] text-ink-muted">
            {stayDays(r)} day{stayDays(r) === 1 ? '' : 's'} · {r.ward_name} · Bed {r.bed_number}
            {dischargeTypeLabel(r.discharge_type) ? ` · ${dischargeTypeLabel(r.discharge_type)}` : ''}
          </div>
        </div>
      ),
    },
    { key: 'summary', header: 'Discharge summary', render: (r) => <SummaryStatusBadge status={r.discharge_summary_status} /> },
    { key: 'casesheet', header: 'Case sheet', render: (r) => <CaseSheetStatusBadge row={r} /> },
    {
      key: 'open', header: '', align: 'right',
      render: (r) => (
        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); onOpen(r); }}>
          Open case sheet
        </Button>
      ),
    },
  ];

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-end gap-2.5">
        <label className="flex min-w-[220px] flex-1 flex-col gap-1 text-[11.5px] font-semibold text-ink-muted">
          Search
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Patient name, UHID or IP number" aria-label="Search discharged admissions" />
        </label>
        <label className="flex flex-col gap-1 text-[11.5px] font-semibold text-ink-muted">
          Discharged from
          <Input type="date" value={from} max={to || undefined} onChange={(e) => { setFrom(e.target.value); setPage(1); }} />
        </label>
        <label className="flex flex-col gap-1 text-[11.5px] font-semibold text-ink-muted">
          to
          <Input type="date" value={to} min={from || undefined} onChange={(e) => { setTo(e.target.value); setPage(1); }} />
        </label>
        {(search || from || to) && (
          <Button size="sm" variant="ghost" onClick={() => { setSearch(''); setQ(''); setFrom(''); setTo(''); setPage(1); }}>
            Clear
          </Button>
        )}
      </div>

      {badRange && (
        <div className="mb-3 rounded-[var(--radius)] bg-warn-soft px-3 py-2 text-xs text-warn-ink">
          The “from” date is after the “to” date, so nothing can match.
        </div>
      )}

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        onRowClick={onOpen}
        isLoading={list.isLoading}
        isError={list.isError}
        errorMessage={list.error instanceof Error ? list.error.message : undefined}
        onRetry={() => list.refetch()}
        emptyLabel={q || from || to ? 'No discharged admissions match' : 'No discharged admissions yet'}
        emptyHint="Shows admissions discharged at this branch."
      />

      {(page > 1 || list.data?.has_more) && (
        <div className="mt-3 flex items-center justify-between text-xs text-ink-muted">
          <span>
            {total != null
              ? `${(page - 1) * PAGE_SIZE + 1}–${(page - 1) * PAGE_SIZE + rows.length} of ${total}`
              : `Page ${page}`}
          </span>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" disabled={page <= 1 || list.isFetching} onClick={() => setPage((p) => p - 1)}>← Previous</Button>
            <Button size="sm" variant="ghost" disabled={!list.data?.has_more || list.isFetching} onClick={() => setPage((p) => p + 1)}>Next →</Button>
          </div>
        </div>
      )}
    </div>
  );
}
